import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { sourcingAPI, getErrorDetail } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import { Loader2, Upload, Send, Search } from 'lucide-react';

function SettingsSection({ settings, isSuperAdmin, onSaved, isProduction, lastTest, onTestSent }) {
  const { user } = useAuth();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirmSendsOpen, setConfirmSendsOpen] = useState(false);
  const [confirmedNoTest, setConfirmedNoTest] = useState(false);
  const [findingOwner, setFindingOwner] = useState(false);
  const [ownerResults, setOwnerResults] = useState(null);
  const [newOwnName, setNewOwnName] = useState('');
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidates, setCandidates] = useState(null);

  useEffect(() => { setForm(settings); }, [settings]);

  const hasLoginMapped = (settings.user_logins || []).some((l) => l.user_id === user?.id);
  const hasSavedRecipient = !!(settings.internal_test_recipient || '').trim();
  const sendTestDisabledReason = !isProduction
    ? 'Production only'
    : !hasSavedRecipient
      ? 'Save an internal test recipient first'
      : !hasLoginMapped
        ? 'Your BrokerBin login is not mapped — ask a Super Admin'
        : null;

  const sendTestMessage = async () => {
    setSendingTest(true);
    setTestResult(null);
    try {
      const res = await sourcingAPI.sendTestMessage();
      setTestResult({ ok: true, message: `Sent to ${res.data.recipient}` });
      toast.success('Test message sent');
      onTestSent && onTestSent();
    } catch (e) {
      const detail = getErrorDetail(e, 'Failed to send test message');
      setTestResult({ ok: false, message: detail });
      toast.error(detail);
    } finally {
      setSendingTest(false);
    }
  };

  const findVstlOwner = async () => {
    setFindingOwner(true);
    setOwnerResults(null);
    try {
      const res = await sourcingAPI.findVstlOwner();
      setOwnerResults(res.data.results || []);
      if (!res.data.results?.length) toast.warning(`No BrokerBin listing found for "${res.data.query}"`);
    } catch (e) {
      toast.error(getErrorDetail(e, 'Failed to look up the VSTL owner id'));
    } finally {
      setFindingOwner(false);
    }
  };

  const applyOwner = (owner) => {
    setForm({ ...form, internal_test_recipient: owner });
    setOwnerResults(null);
    setCandidates(null);
    toast.success('Recipient filled — click Save Settings to use it');
  };

  const loadCandidates = async () => {
    setLoadingCandidates(true);
    setCandidates(null);
    try {
      const res = await sourcingAPI.recipientCandidates();
      setCandidates(res.data);
    } catch (e) {
      toast.error(getErrorDetail(e, 'Failed to load known companies'));
    } finally {
      setLoadingCandidates(false);
    }
  };

  const addOwnName = () => {
    const name = newOwnName.trim();
    if (!name) return;
    const existing = form.own_company_names || [];
    if (existing.includes(name)) { setNewOwnName(''); return; }
    setForm({ ...form, own_company_names: [...existing, name] });
    setNewOwnName('');
  };

  const removeOwnName = (name) => {
    setForm({ ...form, own_company_names: (form.own_company_names || []).filter((n) => n !== name) });
  };

  const toggleSendsEnabled = (v) => {
    if (v && !lastTest) {
      setConfirmSendsOpen(true);
      return;
    }
    setForm({ ...form, sends_enabled: v });
    setConfirmedNoTest(false);
  };

  const confirmSendsAnyway = () => {
    setForm({ ...form, sends_enabled: true });
    setConfirmedNoTest(true);
    setConfirmSendsOpen(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only this section's own fields — never resend user_logins/companies/etc, or a
      // stale copy of them would silently overwrite whatever another section just saved.
      const payload = { sends_enabled: form.sends_enabled, scheduled_scan_enabled: form.scheduled_scan_enabled };
      if (isSuperAdmin) {
        Object.assign(payload, {
          internal_test_recipient: form.internal_test_recipient,
          scan_hour_utc: form.scan_hour_utc,
          daily_call_cap_preview: form.daily_call_cap_preview,
          scan_call_budget: form.scan_call_budget,
          max_pages_per_part: form.max_pages_per_part,
          scan_now_cap_per_user: form.scan_now_cap_per_user,
          live_search_daily_cap: form.live_search_daily_cap,
          min_listing_qty: form.min_listing_qty,
          own_company_names: form.own_company_names,
        });
      }
      if (confirmedNoTest && form.sends_enabled) payload.confirm_no_test_message = true;
      const res = await sourcingAPI.updateSettings(payload);
      toast.success('Settings saved');
      onSaved(res.data);
      setConfirmedNoTest(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-sm" data-testid="sourcing-admin-settings">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Settings</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm">Sending Enabled</p>
            {!isSuperAdmin && <p className="text-xs text-muted-foreground">Super Admin only</p>}
            {isSuperAdmin && !lastTest && <p className="text-xs text-amber-600">No successful internal test yet</p>}
          </div>
          <Switch checked={!!form.sends_enabled} disabled={!isSuperAdmin}
            onCheckedChange={toggleSendsEnabled} data-testid="admin-sends-enabled-switch" />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm">Scheduled Daily Scan</p>
            {!isSuperAdmin && <p className="text-xs text-muted-foreground">Super Admin only · Production only</p>}
          </div>
          <Switch checked={!!form.scheduled_scan_enabled} disabled={!isSuperAdmin}
            onCheckedChange={(v) => setForm({ ...form, scheduled_scan_enabled: v })} data-testid="admin-scheduled-scan-switch" />
        </div>
        {isSuperAdmin && (
          <>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Internal Test Recipient</label>
              <div className="flex items-center gap-2">
                <Input value={form.internal_test_recipient || ''} onChange={(e) => setForm({ ...form, internal_test_recipient: e.target.value })} data-testid="admin-test-recipient-input" />
                <Button type="button" variant="outline" size="sm" className="shrink-0 rounded-sm"
                  disabled={findingOwner} onClick={findVstlOwner} data-testid="admin-find-vstl-owner-btn">
                  {findingOwner ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}
                  Find VSTL owner ID
                </Button>
                <Button type="button" variant="outline" size="sm" className="shrink-0 rounded-sm"
                  disabled={loadingCandidates} onClick={loadCandidates} data-testid="admin-browse-candidates-btn">
                  {loadingCandidates ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Browse known companies
                </Button>
                <Button type="button" variant="outline" size="sm" className="shrink-0 rounded-sm"
                  disabled={!!sendTestDisabledReason || sendingTest} title={sendTestDisabledReason || ''}
                  onClick={sendTestMessage} data-testid="admin-send-test-message-btn">
                  {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                  Send test message
                </Button>
              </div>
              {ownerResults && (
                <div className="mt-1 border border-border rounded-sm divide-y divide-border" data-testid="admin-vstl-owner-results">
                  {ownerResults.map((r, i) => (
                    <div key={i} className="px-2 py-1.5 flex items-center justify-between gap-2 text-xs" data-testid={`admin-vstl-owner-row-${i}`}>
                      <span className="truncate">{r.company} <span className="text-muted-foreground font-mono">({r.owner || 'no owner id'})</span></span>
                      <Button size="sm" variant="ghost" className="h-6 px-2 shrink-0" disabled={!r.owner}
                        onClick={() => applyOwner(r.owner)} data-testid={`admin-vstl-owner-use-btn-${i}`}>Use</Button>
                    </div>
                  ))}
                </div>
              )}
              {candidates && (
                <div className="mt-1 space-y-2" data-testid="admin-recipient-candidates">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">VSTL's own listings (last 35 days)</p>
                    {(candidates.own_listing_owners || []).length === 0 && (
                      <p className="text-xs text-muted-foreground">None spotted yet — run a scan or add a company below.</p>
                    )}
                    <div className="border border-border rounded-sm divide-y divide-border">
                      {(candidates.own_listing_owners || []).map((o, i) => (
                        <div key={i} className="px-2 py-1.5 flex items-center justify-between gap-2 text-xs" data-testid={`admin-own-listing-owner-row-${i}`}>
                          <span className="truncate">{o.company_name} <span className="text-muted-foreground font-mono">({o.owner})</span></span>
                          <Button size="sm" variant="ghost" className="h-6 px-2 shrink-0" onClick={() => applyOwner(o.owner)} data-testid={`admin-own-listing-owner-use-btn-${i}`}>Use</Button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Companies seen in scans ({(candidates.companies || []).length} with a stored owner)</p>
                    <div className="max-h-40 overflow-y-auto border border-border rounded-sm divide-y divide-border">
                      {(candidates.companies || []).map((c) => (
                        <div key={c.id} className="px-2 py-1.5 flex items-center justify-between gap-2 text-xs" data-testid={`admin-candidate-company-row-${c.id}`}>
                          <span className="truncate">{c.name} <span className="text-muted-foreground font-mono">({c.owner})</span></span>
                          <Button size="sm" variant="ghost" className="h-6 px-2 shrink-0" onClick={() => applyOwner(c.owner)} data-testid={`admin-candidate-company-use-btn-${c.id}`}>Use</Button>
                        </div>
                      ))}
                      {(candidates.companies || []).length === 0 && <p className="text-xs text-muted-foreground px-2 py-1.5">None yet.</p>}
                    </div>
                  </div>
                </div>
              )}
              {sendTestDisabledReason && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="admin-send-test-disabled-reason">Send test message: {sendTestDisabledReason}</p>
              )}
              {testResult && (
                <p className={`text-xs mt-1 ${testResult.ok ? 'text-emerald-600' : 'text-red-600'}`} data-testid="admin-send-test-result">
                  {testResult.message}
                </p>
              )}
              {lastTest && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="admin-last-test-info">
                  Last test: {new Date(lastTest.sent_at).toLocaleString()} → {lastTest.recipient || '—'} ({lastTest.result || 'success'})
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                The recipient is a BrokerBin company owner ID, not a login name. Use "Find VSTL owner ID" next to the input to look it up. After a successful test, switch on Sends enabled.
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Own Company Names</label>
              <div className="flex items-center gap-2">
                <Input placeholder="Add a company name..." value={newOwnName}
                  onChange={(e) => setNewOwnName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOwnName(); } }}
                  data-testid="admin-own-company-name-input" />
                <Button type="button" variant="outline" size="sm" className="shrink-0 rounded-sm"
                  onClick={addOwnName} disabled={!newOwnName.trim()} data-testid="admin-own-company-name-add-btn">Add</Button>
              </div>
              {(form.own_company_names || []).length > 0 && (
                <div className="mt-1 border border-border rounded-sm divide-y divide-border" data-testid="admin-own-company-names-list">
                  {(form.own_company_names || []).map((name, i) => (
                    <div key={name} className="px-2 py-1.5 flex items-center justify-between gap-2 text-xs" data-testid={`admin-own-company-name-row-${i}`}>
                      <span className="truncate">{name}</span>
                      <Button size="sm" variant="ghost" className="h-6 px-2 shrink-0"
                        onClick={() => removeOwnName(name)} data-testid={`admin-own-company-name-remove-btn-${i}`}>Remove</Button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Exactly as the company name appears on BrokerBin listings, e.g. Value Smart Trading Ltd. Used by "Find VSTL owner ID" to look up VSTL's own BrokerBin company owner ID. Click Save Settings after adding or removing a name.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Scan Hour (UTC)</label>
                <Input type="number" value={form.scan_hour_utc ?? 1} onChange={(e) => setForm({ ...form, scan_hour_utc: Number(e.target.value) })} data-testid="admin-scan-hour-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Daily Call Cap (Preview)</label>
                <Input type="number" value={form.daily_call_cap_preview ?? 100} onChange={(e) => setForm({ ...form, daily_call_cap_preview: Number(e.target.value) })} data-testid="admin-cap-preview-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Daily Scan Call Budget</label>
                <Input type="number" value={form.scan_call_budget ?? 450} onChange={(e) => setForm({ ...form, scan_call_budget: Number(e.target.value) })} data-testid="admin-scan-call-budget-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Max Pages Per Part</label>
                <Input type="number" value={form.max_pages_per_part ?? 1} onChange={(e) => setForm({ ...form, max_pages_per_part: Number(e.target.value) })} data-testid="admin-max-pages-input" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              The daily scan stops at whichever comes first: this budget, or the "scan stop %" of the daily call cap. HOT parts are scanned first, so a budget-limited scan always covers the most important parts; the rest roll to tomorrow. Max Pages Per Part is informational for now — each part still uses 1 call per scan; a part with &gt;100 listings (page 2+) shows up in the scan report.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Live Search Cap Per User</label>
                <Input type="number" value={form.scan_now_cap_per_user ?? 5} onChange={(e) => setForm({ ...form, scan_now_cap_per_user: Number(e.target.value) })} data-testid="admin-scan-now-cap-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Live Search Cap (Everyone)</label>
                <Input type="number" value={form.live_search_daily_cap ?? 100} onChange={(e) => setForm({ ...form, live_search_daily_cap: Number(e.target.value) })} data-testid="admin-live-search-cap-input" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Shared by live SKU search and vendor stock pull — per-user cap, then a total cap across everyone for the day.
            </p>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Minimum Listing Quantity</label>
              <Input type="number" value={form.min_listing_qty ?? 10} onChange={(e) => setForm({ ...form, min_listing_qty: Number(e.target.value) })} data-testid="admin-min-listing-qty-input" />
              <p className="text-xs text-muted-foreground mt-1">
                A listing below this still becomes a draft line like any other — flagged "Low qty" and unticked by default, sorted after the normal lines, so the purchase person decides whether to include it. A draft with only low-qty lines is still created, marked "Low qty only". BrokerBin's search call has no quantity filter, so this is purely a draft-review aid, not a call saver.
              </p>
            </div>
          </>
        )}
        <Button onClick={save} disabled={saving} data-testid="admin-settings-save-btn">{saving ? 'Saving...' : 'Save Settings'}</Button>
      </CardContent>

      <AlertDialog open={confirmSendsOpen} onOpenChange={setConfirmSendsOpen}>
        <AlertDialogContent data-testid="admin-confirm-sends-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Turn on Sends without a successful test?</AlertDialogTitle>
            <AlertDialogDescription>
              No internal test message has succeeded yet. Sending real BrokerBin messages without
              first confirming connectivity can fail silently. Send a test message first, or confirm
              to turn Sends on anyway.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-confirm-sends-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSendsAnyway} data-testid="admin-confirm-sends-anyway">Turn on anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function LoginMappingsSection({ settings, onSaved }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loginValue, setLoginValue] = useState('');
  const [picked, setPicked] = useState(null);
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [knownUsers, setKnownUsers] = useState({});

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (query.length < 2) { setResults([]); return; }
      try {
        const res = await api.get('/users/search', { params: { q: query } });
        const users = res.data || [];
        setResults(users);
        setKnownUsers((prev) => ({ ...prev, ...Object.fromEntries(users.map((u) => [u.id, u])) }));
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    // Round (3 Nov 2026, item 5): resolve names for mappings already saved before this
    // session ever searched for them — same pattern as RegionSendersSection below.
    const names = settings.user_logins_names || {};
    if (Object.keys(names).length > 0) {
      setKnownUsers((prev) => ({
        ...Object.fromEntries(Object.entries(names).map(([id, v]) => [id, { id, name: v.name, email: v.email }])),
        ...prev,
      }));
    }
  }, [settings.user_logins_names]);

  const userLogins = settings.user_logins || [];

  const save = async () => {
    if (!picked || !loginValue.trim()) return;
    setSaving(true);
    try {
      const next = userLogins.filter((r) => r.user_id !== picked.id);
      next.push({ user_id: picked.id, brokerbin_login: loginValue.trim(), is_default: isDefault });
      const final = isDefault ? next.map((r) => ({ ...r, is_default: r.user_id === picked.id })) : next;
      const res = await sourcingAPI.updateSettings({ user_logins: final });
      toast.success(`Mapped ${picked.name} → ${loginValue.trim()}`);
      onSaved(res.data);
      setPicked(null); setLoginValue(''); setQuery(''); setResults([]); setIsDefault(false);
    } catch (e) {
      toast.error('Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (uid) => {
    const next = userLogins.filter((r) => r.user_id !== uid);
    try {
      const res = await sourcingAPI.updateSettings({ user_logins: next });
      toast.success('Mapping removed');
      onSaved(res.data);
    } catch {
      toast.error('Failed to remove mapping');
    }
  };

  return (
    <Card className="rounded-sm" data-testid="sourcing-admin-login-mappings">
      <CardHeader className="pb-2"><CardTitle className="text-sm">BrokerBin Login Mappings</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Input placeholder="Search a Trade IQ user..." value={picked ? picked.name : query}
            onChange={(e) => { setPicked(null); setQuery(e.target.value); }} data-testid="login-mapping-user-search" />
          {results.length > 0 && !picked && (
            <div className="absolute z-10 w-full bg-card border border-border rounded-sm mt-1 max-h-48 overflow-y-auto shadow-lg">
              {results.map((u) => (
                <div key={u.id} className="px-3 py-2 text-sm hover:bg-muted cursor-pointer" onClick={() => { setPicked(u); setResults([]); }} data-testid={`login-mapping-user-option-${u.id}`}>
                  {u.name} <span className="text-muted-foreground text-xs">{u.email}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <Input placeholder="BrokerBin login (username)" value={loginValue} onChange={(e) => setLoginValue(e.target.value)} data-testid="login-mapping-login-input" />
        <label className="flex items-center gap-2 text-xs">
          <Switch checked={isDefault} onCheckedChange={setIsDefault} data-testid="login-mapping-default-switch" /> Default login (used for the scheduled scan and unmapped senders)
        </label>
        <Button size="sm" onClick={save} disabled={saving || !picked || !loginValue.trim()} data-testid="login-mapping-save-btn">Save Mapping</Button>
        <div className="space-y-1 pt-2">
          {userLogins.map((row) => (
            <div key={row.user_id} className="text-xs flex items-center justify-between gap-2" data-testid={`login-mapping-row-${row.user_id}`}>
              <span className="text-muted-foreground" data-testid={`login-mapping-label-${row.user_id}`}>
                {knownUsers[row.user_id]?.name
                  ? `${knownUsers[row.user_id].name} (${knownUsers[row.user_id].email}) - ${row.user_id}`
                  : row.user_id} → <span className="font-mono">{row.brokerbin_login}</span>
                {row.is_default && <Badge variant="outline" className="ml-2">Default</Badge>}
              </span>
              <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => remove(row.user_id)} data-testid={`login-mapping-remove-btn-${row.user_id}`}>Remove</Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function RegionSendersSection({ settings, onSaved }) {
  const assigned = settings.unowned_sender_by_region || {};
  const [knownUsers, setKnownUsers] = useState({});

  useEffect(() => {
    const names = settings.unowned_sender_by_region_names || {};
    if (Object.keys(names).length > 0) {
      setKnownUsers((prev) => ({
        ...Object.fromEntries(Object.entries(names).map(([id, name]) => [id, { id, name }])),
        ...prev,
      }));
    }
  }, [settings.unowned_sender_by_region_names]);

  const save = async (regionKey, userId) => {
    const next = { ...assigned, [regionKey]: userId };
    const res = await sourcingAPI.updateSettings({ unowned_sender_by_region: next });
    onSaved(res.data);
    return res;
  };

  const rows = [
    { key: 'North America', label: 'North America' },
    { key: 'Europe', label: 'Europe' },
    { key: 'Other', label: 'All other regions' },
  ];

  return (
    <Card className="rounded-sm" data-testid="sourcing-admin-region-senders">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Regional Senders</CardTitle></CardHeader>
      <CardContent className="divide-y divide-border">
        <p className="text-xs text-muted-foreground pb-2">
          Who gets a vendor's draft when BrokerBin has no owner mapped and the company isn't linked to a Zoho vendor.
        </p>
        {rows.map((r) => (
          <RegionSenderRowControlled key={r.key} regionKey={r.key} label={r.label} userId={assigned[r.key]}
            knownUsers={knownUsers} setKnownUsers={setKnownUsers} onSave={save} />
        ))}
      </CardContent>
    </Card>
  );
}

function RegionSenderRowControlled({ regionKey, label, userId, knownUsers, setKnownUsers, onSave }) {
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const current = userId ? knownUsers[userId] : null;

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (query.length < 2) { setResults([]); return; }
      try {
        const res = await api.get('/users/search', { params: { q: query } });
        const users = res.data || [];
        setResults(users);
        setKnownUsers((prev) => ({ ...prev, ...Object.fromEntries(users.map((u) => [u.id, u])) }));
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, setKnownUsers]);

  const pick = async (u) => {
    setSaving(true);
    try {
      await onSave(regionKey, u.id);
      setKnownUsers((prev) => ({ ...prev, [u.id]: u }));
      toast.success(`${label} → ${u.name}`);
      setEditing(false); setQuery(''); setResults([]);
    } catch {
      toast.error(`Failed to set ${label} sender`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 text-sm py-2" data-testid={`region-sender-row-${regionKey}`}>
      <span className="text-muted-foreground shrink-0 w-40">{label}</span>
      {!editing ? (
        <div className="flex items-center gap-2 flex-1 justify-end">
          <span data-testid={`region-sender-current-${regionKey}`}>{current?.name || (userId ? userId : 'Not set')}</span>
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`region-sender-edit-btn-${regionKey}`}>Change</Button>
        </div>
      ) : (
        <div className="relative flex-1">
          <Input autoFocus placeholder="Search a Trade IQ user..." value={query} onChange={(e) => setQuery(e.target.value)}
            disabled={saving} data-testid={`region-sender-search-${regionKey}`} />
          {results.length > 0 && (
            <div className="absolute z-10 w-full bg-card border border-border rounded-sm mt-1 max-h-48 overflow-y-auto shadow-lg">
              {results.map((u) => (
                <div key={u.id} className="px-3 py-2 text-sm hover:bg-muted cursor-pointer" onClick={() => pick(u)} data-testid={`region-sender-option-${regionKey}-${u.id}`}>
                  {u.name} <span className="text-muted-foreground text-xs">{u.email}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {editing && (
        <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setQuery(''); setResults([]); }} data-testid={`region-sender-cancel-btn-${regionKey}`}>Cancel</Button>
      )}
    </div>
  );
}

function CompaniesSection() {
  const [q, setQ] = useState('');
  const [companies, setCompanies] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [vendorQuery, setVendorQuery] = useState('');
  const [vendorResults, setVendorResults] = useState([]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const res = await sourcingAPI.listCompanies(q);
        setCompanies(res.data.companies);
      } catch { toast.error('Failed to search companies'); }
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  const toggle = async (c, field) => {
    try {
      const res = await sourcingAPI.updateCompany(c.id, { [field]: !c[field] });
      setCompanies((prev) => prev.map((x) => (x.id === c.id ? { ...res.data, status: x.status, matched_vendor_name: x.matched_vendor_name } : x)));
    } catch { toast.error('Failed to update company'); }
  };

  const link = async (c, vendorId, vendorName) => {
    try {
      const res = await sourcingAPI.updateCompany(c.id, { zoho_vendor_id: vendorId });
      setCompanies((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...res.data, status: 'MATCHED', matched_vendor_name: vendorName } : x)));
      toast.success(`Linked to ${vendorName}`);
      setExpanded(null);
      setVendorQuery(''); setVendorResults([]);
    } catch { toast.error('Failed to link vendor'); }
  };

  const openMatcher = async (c) => {
    if (expanded === c.id) { setExpanded(null); return; }
    setExpanded(c.id);
    setVendorQuery(''); setVendorResults([]);
    if (!suggestions[c.id]) {
      try {
        const res = await sourcingAPI.companySuggestions(c.id);
        setSuggestions((prev) => ({ ...prev, [c.id]: res.data.suggestions || [] }));
      } catch { setSuggestions((prev) => ({ ...prev, [c.id]: [] })); }
    }
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (vendorQuery.length < 2) { setVendorResults([]); return; }
      try {
        const res = await sourcingAPI.zohoVendorSearch(vendorQuery);
        setVendorResults(res.data.vendors || []);
      } catch { setVendorResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [vendorQuery]);

  const statusBadge = (status) => {
    const variant = status === 'MATCHED' ? 'default' : status === 'UNMATCHED' ? 'destructive' : 'outline';
    return <Badge variant={variant} className="text-[10px]">{status}</Badge>;
  };

  return (
    <Card className="rounded-sm" data-testid="sourcing-admin-companies">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Companies</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <Input placeholder="Search companies..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-companies-search" />
        <div className="divide-y divide-border">
          {companies.map((c) => (
            <div key={c.id} className="py-2 space-y-1.5" data-testid={`admin-company-row-${c.id}`}>
              <div className="flex items-center justify-between gap-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="truncate">{c.name}</span>
                  {statusBadge(c.status)}
                  {c.matched_vendor_name && <span className="text-xs text-muted-foreground truncate">→ {c.matched_vendor_name}</span>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => toggle(c, 'opted_out')} data-testid={`admin-company-optout-btn-${c.id}`}>
                    {c.opted_out ? 'Un-opt-out' : 'Opt Out'}
                  </Button>
                  {c.status !== 'OWN' && (
                    <Button size="sm" variant="outline" onClick={() => openMatcher(c)} data-testid={`admin-company-match-btn-${c.id}`}>
                      {c.status === 'MATCHED' ? 'Re-match' : 'Match to Zoho vendor'}
                    </Button>
                  )}
                </div>
              </div>
              {expanded === c.id && (
                <div className="ml-2 pl-2 border-l-2 border-border space-y-2" data-testid={`admin-company-matcher-${c.id}`}>
                  {suggestions[c.id] && suggestions[c.id].length === 0 && (
                    <p className="text-xs text-muted-foreground">No automatic suggestions — search manually below.</p>
                  )}
                  {(suggestions[c.id] || []).length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground">Suggestions (never applied automatically):</p>
                      <div className="border border-border rounded-sm divide-y divide-border">
                        {suggestions[c.id].map((s, i) => (
                          <div key={i} className="px-2 py-1 flex items-center justify-between text-xs" data-testid={`admin-company-suggestion-${c.id}-${i}`}>
                            <span className="truncate">{s.vendor_name} <span className="text-muted-foreground">({s.score})</span></span>
                            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => link(c, s.zoho_vendor_id, s.vendor_name)} data-testid={`admin-company-suggestion-accept-${c.id}-${i}`}>Accept</Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="relative">
                    <Input placeholder="Search Zoho vendors..." value={vendorQuery} onChange={(e) => setVendorQuery(e.target.value)} data-testid={`admin-company-vendor-search-${c.id}`} />
                    {vendorResults.length > 0 && (
                      <div className="border border-border rounded-sm mt-1 max-h-32 overflow-y-auto divide-y divide-border">
                        {vendorResults.map((v) => (
                          <div key={v.zoho_vendor_id} className="px-2 py-1.5 text-xs hover:bg-muted cursor-pointer flex items-center justify-between"
                            onClick={() => link(c, v.zoho_vendor_id, v.vendor_name)} data-testid={`admin-company-vendor-option-${c.id}-${v.zoho_vendor_id}`}>
                            <span>{v.vendor_name}</span>
                            <span className="text-muted-foreground">{v.country}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          {companies.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No companies yet.</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function PartsUploadSection() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const doPreview = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await sourcingAPI.uploadPartsPreview(file);
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to read this file');
    } finally {
      setUploading(false);
    }
  };

  const confirm = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const res = await sourcingAPI.confirmPartsUpload(preview.preview_id);
      toast.success(`Applied ${res.data.applied} part(s)`);
      setPreview(null);
      setFile(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm upload');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Card className="rounded-sm" data-testid="sourcing-admin-parts-upload">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Upload Search Parts (XLSX)</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">Columns: sku, search_part. Unknown SKUs are rejected with a reason before anything is applied.</p>
        <div className="flex gap-2">
          <Input type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} data-testid="parts-upload-file-input" />
          <Button onClick={doPreview} disabled={!file || uploading} data-testid="parts-upload-preview-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          </Button>
        </div>
        {preview && (
          <div className="space-y-2" data-testid="parts-upload-preview-result">
            <p className="text-sm">{preview.valid_count} valid · {preview.invalid_count} invalid</p>
            {preview.invalid.length > 0 && (
              <div className="max-h-32 overflow-y-auto text-xs text-destructive space-y-0.5">
                {preview.invalid.map((r, i) => (
                  <p key={i} data-testid={`parts-upload-invalid-row-${i}`}>Row {r.row_index}: {r.sku_id || '(no sku)'} — {r.reason}</p>
                ))}
              </div>
            )}
            <Button size="sm" onClick={confirm} disabled={confirming || preview.valid_count === 0} data-testid="parts-upload-confirm-btn">
              {confirming ? 'Applying...' : `Apply ${preview.valid_count} part(s)`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InboxSection() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sourcingAPI.adminInbox()
      .then((res) => setMessages(res.data.messages))
      .catch(() => toast.error('Failed to load Inbox'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card className="rounded-sm" data-testid="sourcing-admin-inbox">
      <CardHeader className="pb-2"><CardTitle className="text-sm">BrokerBin Inbox</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {!loading && messages.length === 0 && <p className="text-sm text-muted-foreground py-2">No inbound BrokerBin messages yet.</p>}
        <div className="divide-y divide-border">
          {messages.map((m) => (
            <div key={m.id} className="py-2 text-xs space-y-1" data-testid={`inbox-row-${m.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">{new Date(m.received_at).toLocaleString()}</span>
                {m.matched_rfq_id ? <Badge variant="outline">Matched RFQ</Badge> : <Badge variant="outline">Unmatched</Badge>}
              </div>
              <p className="font-mono truncate text-[11px] text-muted-foreground">{m.raw_body?.slice(0, 200)}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ScanReportSection() {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await sourcingAPI.scanReport();
        setDays((res.data.days || []).slice().reverse());  // most recent day first
      } catch {
        toast.error('Failed to load scan report');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Card className="rounded-sm" data-testid="admin-scan-report">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Scan Report — last 14 days</CardTitle>
        <p className="text-xs text-muted-foreground">
          For deciding on a bigger BrokerBin plan. Read-only, from scans, call usage and stored listings.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="admin-scan-report-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1 pr-2">Day</th>
                  <th className="py-1 pr-2 text-right">Parts Searched</th>
                  <th className="py-1 pr-2 text-right">Calls Used</th>
                  <th className="py-1 pr-2 text-right">0 Listings</th>
                  <th className="py-1 pr-2 text-right">&gt;100 Listings</th>
                  <th className="py-1 pr-2 text-right">Listings Stored</th>
                  <th className="py-1 pr-2 text-right">Below Min Qty</th>
                  <th className="py-1 pr-2 text-right">Matched</th>
                  <th className="py-1 pr-2 text-right">Unmatched</th>
                  <th className="py-1 pr-2 text-right">Drafts</th>
                  <th className="py-1 pr-2 text-right">RFQs Sent</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => (
                  <tr key={d.date_key} className="border-b border-border/50" data-testid={`admin-scan-report-row-${d.date_key}`}>
                    <td className="py-1 pr-2">{d.date_key}</td>
                    <td className="py-1 pr-2 text-right">{d.parts_searched}</td>
                    <td className="py-1 pr-2 text-right" title={Object.entries(d.calls_by_kind || {}).map(([k, v]) => `${k}: ${v}`).join(', ') || 'none'}>
                      {d.calls_used}
                    </td>
                    <td className="py-1 pr-2 text-right">{d.parts_zero_listings}</td>
                    <td className="py-1 pr-2 text-right">{d.parts_over_100_listings}</td>
                    <td className="py-1 pr-2 text-right">{d.listings_stored}</td>
                    <td className="py-1 pr-2 text-right">{d.listings_below_min_qty}</td>
                    <td className="py-1 pr-2 text-right">{d.listings_matched}</td>
                    <td className="py-1 pr-2 text-right">{d.listings_unmatched}</td>
                    <td className="py-1 pr-2 text-right">{d.drafts_created}</td>
                    <td className="py-1 pr-2 text-right">{d.rfqs_sent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function AdminTab() {
  const { user, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState([]);
  const [lastTest, setLastTest] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([sourcingAPI.getAdminSettings(), sourcingAPI.adminUsage()]);
      setSettings(s.data);
      setUsage(u.data.usage);
      if (isSuperAdmin) {
        try {
          const t = await sourcingAPI.listRfqs({ view: 'all', status: 'TEST' });
          const rows = (t.data.rfqs || []).filter((row) => row.result === 'success')
            .sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));
          setLastTest(rows[0] || null);
        } catch { setLastTest(null); }
      }
    } catch (e) {
      toast.error('Failed to load Admin data');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  if (loading || !settings) return <div className="flex justify-center py-16" data-testid="admin-loading"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="sourcing-admin-tab">
      <SettingsSection settings={settings} isSuperAdmin={isSuperAdmin} onSaved={setSettings}
        isProduction={user?.app_env === 'production'} lastTest={lastTest} onTestSent={load} />
      <LoginMappingsSection settings={settings} onSaved={setSettings} />
      <RegionSendersSection settings={settings} onSaved={setSettings} />
      <PartsUploadSection />
      <CompaniesSection />
      <InboxSection />
      <Card className="rounded-sm" data-testid="sourcing-admin-usage">
        <CardHeader className="pb-2"><CardTitle className="text-sm">BrokerBin Call Usage</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          {usage.map((u) => (
            <div key={u.date_key} className="text-sm" data-testid={`admin-usage-row-${u.date_key}`}>
              <div className="flex justify-between">
                <span>{u.date_key}</span>
                <span data-testid={`admin-usage-our-calls-${u.date_key}`}>Our calls today {u.calls_today || 0} / {u.limit}</span>
              </div>
              {u.bb_count != null && u.bb_limit != null && (
                <div className="flex justify-between text-xs text-muted-foreground" data-testid={`admin-usage-bb-counter-${u.date_key}`}>
                  <span>BrokerBin counter</span>
                  <span>{u.bb_count} / {u.bb_limit}{u.bb_seen_at ? ` (as of ${new Date(u.bb_seen_at).toLocaleTimeString()})` : ''}</span>
                </div>
              )}
              {u.calls_by_kind && Object.keys(u.calls_by_kind).length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {Object.entries(u.calls_by_kind).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                </p>
              )}
            </div>
          ))}
          {usage.length === 0 && <p className="text-sm text-muted-foreground">No BrokerBin calls recorded yet today.</p>}
        </CardContent>
      </Card>
      {isSuperAdmin && <ScanReportSection />}
    </div>
  );
}
